import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { delay, filter, takeUntil } from 'rxjs/operators';
import { UserPermissionsModel } from 'src/app/core/backend/user/model/userPermissions.model';
import { AuthStoreService } from '../../../core/auth/auth.service';
import { UserBackendModel } from '../../../core/backend/customer/model/UserBackend.model';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  activeTab$: BehaviorSubject<string>;
  currentUser$: Observable<UserBackendModel>;
  private ngUnsubscribe$: Subject<any> = new Subject<any>();
  userRolID: number;
  userPermissions: {};
  viewTime: Boolean;
  viewJobs: Boolean;
  viewWellLogs: Boolean;
  viewUsers: Boolean;
  viewRoles: Boolean;
  viewReceiptCapture: Boolean;

  @Input() pageUrl = '';

  constructor(
    private router: Router,
    private authStoreService: AuthStoreService
  ) {}

  ngOnInit(): void {
    const currentTab = localStorage.getItem('activeTab') || 'jobs/view-all';

    this.activeTab$ = new BehaviorSubject<string>(currentTab);

    this.activeTab$
      .pipe(
        filter((data) => !!data),
        // A BehaviorSubject replays its seed to every new subscriber, so this
        // pipe fires once the moment the navbar mounts, not just on nav clicks.
        // That replay is what used to bounce a deep link (e.g. /receipt-capture)
        // to the stored tab. Gate ONLY that first emission on being at a landing
        // route: it can then never override a deep link, a refresh, or the back
        // button. Every later emission is a real onNavChange() click and always
        // navigates, on any route.
        //
        // In practice the auth flow already routes to /jobs/view-all before this
        // component mounts, so the seed emission is always suppressed today. The
        // landing-route check is kept rather than a blanket skip(1) so that the
        // resume-last-tab intent still works if the landing behaviour changes.
        filter((_, index) => index > 0 || this.isOnLandingRoute()),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((url) => this.router.navigate([`/${url}`]));

    this.currentUser$ = this.authStoreService.getCurrentUserInfo();
    this.currentUser$
      .pipe(
        filter((data) => !!data),
        delay(500),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((userData) => {
        this.userRolID = userData.employee_role_id;
        this.userPermissions = JSON.parse(userData.password);
        this.viewTime = this.viewJobs = this.viewWellLogs = this.viewUsers = this.viewReceiptCapture = false;
        for (let i = 0; i < Object.keys(this.userPermissions).length; i++) {
          const id: number = this.userPermissions[i].permission_id;
          const view: 'None' | 'Yes' = this.userPermissions[i]
            .permission_view_option;

          switch (id) {
            case 1:
              if (view !== 'None') {
                this.viewTime = true;
              }
              break;
            case 4:
              if (view !== 'None') {
                this.viewJobs = true;
              }
              break;
            case 16:
              if (view == 'Yes') {
                this.viewWellLogs = true;
              }
              break;
            case 18:
              if (view == 'Yes') {
                this.viewUsers = true;
              }
              break;
            case 20:
              if (view != 'None') {
                this.viewReceiptCapture = true;
              }
              break;
          }
        }
      });
  }

  // True only on the post-login landing routes, where resuming the last-used
  // tab is the intended behaviour. Anywhere else the user asked for a specific
  // URL (deep link, refresh, back button) and the navbar must not redirect.
  // router.url includes any query string / fragment, so compare on the path.
  private isOnLandingRoute(): boolean {
    const path = this.router.url.split(/[?#]/)[0].replace(/\/+$/, '');
    return path === '' || path === '/login';
  }

  onNavChange(navUrl: string) {
    localStorage.setItem('activeTab', navUrl);

    this.activeTab$.next(navUrl);
  }

  onLogout() {
    this.authStoreService.initLogout();
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
